/**
 * communityVideosService — única camada que fala com o Supabase para vídeos.
 * Mapeia o formato cru do banco (snake_case) para o boundary CommunityVideo
 * (camelCase + avatar resolvido). Isola o backend: a UI nunca vê o Supabase.
 */

import { Platform } from 'react-native';
import { File } from 'expo-file-system';
import * as FileSystem from 'expo-file-system/legacy';
import * as ImagePicker from 'expo-image-picker';
import * as VideoThumbnails from 'expo-video-thumbnails';
import { supabase } from '../../../lib/supabase';
import { getCachedUser } from '../../../services/userCacheService';
import { withTimeout } from '../../../utils/community/async';
import { VIDEO_PAGE_SIZE, VIDEO_SOURCE } from '../constants';

const UPLOAD_BUCKET = 'community-videos';
const MAX_VIDEO_DURATION_SECONDS = 60;

const MIME_TO_EXT = { 'video/mp4': 'mp4', 'video/quicktime': 'mov', 'video/webm': 'webm' };

const uriToUploadBody = async (uri) => {
  const file = new File(uri);
  if (!file.exists) throw new Error('Arquivo local não encontrado para upload.');
  const bytes = await file.bytes();
  return bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
    ? bytes.buffer
    : bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
};

// Resolução de avatar idêntica ao resto do app (foto enviada → DiceBear pelo seed).
const getDiceBearUrl = (seed) =>
  `https://api.dicebear.com/7.x/fun-emoji/png?seed=${encodeURIComponent(seed || 'happy')}&size=120`;

/** Linha do RPC get_community_videos (inclui autor via join). @returns {import('../types').CommunityVideo} */
const mapFeedRow = (r) => ({
  id: r.id,
  userId: r.user_id,
  source: r.source,
  youtubeId: r.youtube_id,
  storagePath: r.storage_path,
  thumbnailUrl: r.thumbnail_url,
  title: r.title,
  description: r.description,
  topic: r.topic,
  area: r.area,
  durationSeconds: r.duration_seconds,
  status: r.status,
  section: r.section,
  viewsCount: r.views_count ?? 0,
  votesCount: r.votes_count ?? 0,
  commentsCount: r.comments_count ?? 0,
  relevanceScore: r.relevance_score ?? 0,
  createdAt: r.created_at,
  author: {
    name: r.author_name || 'Usuário',
    username: r.author_username || 'usuario',
    avatarUrl: r.author_foto_url || getDiceBearUrl(r.author_avatar_seed),
  },
});

/**
 * Feed paginado por keyset.
 * @param {{ sort:'recent'|'relevant', topic?:string|null, cursor?:import('../types').VideoCursor|null, limit?:number }} args
 * @returns {Promise<import('../types').VideoPage>}
 */
export const fetchVideos = async ({ sort, section, topic = null, cursor = null, limit = VIDEO_PAGE_SIZE }) => {
  const { data, error } = await supabase.rpc('get_community_videos', {
    p_sort: sort,
    p_section: section,
    p_topic: topic,
    p_cursor_created_at: cursor?.createdAt ?? null,
    p_cursor_votes: cursor?.votes ?? null,
    p_cursor_id: cursor?.id ?? null,
    p_limit: limit,
  });
  if (error) throw error;

  const items = (data || []).map(mapFeedRow);
  const last = items[items.length - 1];
  // Só há próxima página se a página veio cheia (heurística keyset padrão).
  const nextCursor =
    items.length === limit && last
      ? { createdAt: last.createdAt, votes: last.votesCount, id: last.id }
      : null;

  return { items, nextCursor };
};

/**
 * Cria um vídeo do YouTube. Retorna a linha base (sem join de autor — o autor é
 * o usuário atual, anexado na camada otimista).
 */
export const createYoutubeVideo = async ({ youtubeId, title, description = null, thumbnailUrl = null, topic = null, area = null, durationSeconds = null, section = 'updates' }) => {
  const user = await getCachedUser();
  if (!user) throw new Error('Usuário não autenticado');

  const { data, error } = await withTimeout(
    supabase
      .from('community_videos')
      .insert({
        user_id: user.id,
        source: VIDEO_SOURCE.YOUTUBE,
        youtube_id: youtubeId,
        title,
        description,
        thumbnail_url: thumbnailUrl,
        topic,
        area,
        duration_seconds: durationSeconds,
        section,
      })
      .select('id, created_at, votes_count, views_count, comments_count')
      .single(),
    20000,
    'A publicação demorou demais. Verifique no feed se o vídeo já apareceu antes de tentar de novo.',
  );

  if (error) throw error;
  return data; // { id, created_at, votes_count, ... }
};

/** Curtir/descurtir (a tabela de likes; o trigger mantém votes_count). */
export const toggleVideoLike = async (videoId, liked) => {
  const user = await getCachedUser();
  if (!user) throw new Error('Usuário não autenticado');

  if (liked) {
    const { error } = await supabase
      .from('community_video_likes')
      .insert({ video_id: videoId, user_id: user.id });
    // 23505 = já curtido (corrida); idempotente, ignora.
    if (error && error.code !== '23505') throw error;
  } else {
    const { error } = await supabase
      .from('community_video_likes')
      .delete()
      .eq('video_id', videoId)
      .eq('user_id', user.id);
    if (error) throw error;
  }
};

/** IDs de vídeos curtidos pelo usuário atual (para estado "is liked"). */
export const fetchUserVideoLikes = async () => {
  const user = await getCachedUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('community_video_likes')
    .select('video_id')
    .eq('user_id', user.id);
  if (error) throw error;
  return (data || []).map((row) => row.video_id);
};

/** Incrementa visualização (idempotência/throttle fica no client). */
export const incrementVideoView = async (videoId) => {
  const { error } = await supabase.rpc('increment_video_view', { p_video_id: videoId });
  if (error) throw error;
};

/** Denúncia (5+ → takedown automático via trigger). */
export const reportVideo = async (videoId, reason = null) => {
  const user = await getCachedUser();
  if (!user) throw new Error('Usuário não autenticado');
  const { error } = await supabase
    .from('community_video_reports')
    .insert({ video_id: videoId, reporter_id: user.id, reason });
  if (error && error.code !== '23505') throw error; // já denunciado → ignora
};

/** Escolhe um vídeo da galeria, validando o limite de duração (≤60s). */
export const pickGalleryVideo = async () => {
  const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
  if (!perm?.granted) throw new Error('Permissão de galeria negada.');

  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ['videos'],
    allowsMultipleSelection: false,
    // iOS: abre o trimmer nativo (usuário escolhe o trecho ≤60s). Android: não
    // suporta corte de vídeo no picker → mantém false e validamos a duração.
    allowsEditing: Platform.OS === 'ios',
    quality: 0.5, // arquivo menor → upload mais rápido
    videoMaxDuration: MAX_VIDEO_DURATION_SECONDS,
  });
  if (result?.canceled) return null;

  const asset = result?.assets?.[0];
  if (!asset?.uri) throw new Error('Nenhum vídeo selecionado.');

  // Rede de segurança: se ainda vier > 60s (Android sem trim, ou trim não aplicado).
  const durationSeconds = asset.duration ? Math.round(asset.duration / 1000) : null;
  if (durationSeconds && durationSeconds > MAX_VIDEO_DURATION_SECONDS) {
    throw new Error(
      'O vídeo tem mais de 1 minuto. No iPhone, corte na própria seleção; no Android, corte antes na galeria do celular e tente de novo.'
    );
  }
  return asset;
};

const publicUrl = (path) =>
  path ? supabase.storage.from(UPLOAD_BUCKET).getPublicUrl(path).data.publicUrl : null;

/** Gera 1 frame do vídeo e o envia como capa. Best-effort (sem ela, usa placeholder). */
// withTimeout em cada await: o fetch do RN não tem timeout nativo e o
// getThumbnailAsync pode estolar em certos vídeos — sem isto, a Promise pendura
// e o botão fica em "Publicando..." para sempre (mesmo bug do CreatePostScreen).
const generateAndUploadThumbnail = async (videoUri, thumbPath) => {
  try {
    const { uri } = await withTimeout(
      VideoThumbnails.getThumbnailAsync(videoUri, { time: 1000, quality: 0.6 }),
      10000,
    );
    const body = await uriToUploadBody(uri);
    const { error } = await withTimeout(
      supabase.storage
        .from(UPLOAD_BUCKET)
        .upload(thumbPath, body, { contentType: 'image/jpeg', cacheControl: '86400', upsert: false }),
      15000,
    );
    return error ? null : publicUrl(thumbPath);
  } catch {
    return null;
  }
};

/**
 * Upload do vídeo próprio + capa + cria a linha (source='upload').
 * O vídeo sobe em STREAMING (FileSystem.uploadAsync) — não carrega o arquivo
 * inteiro na memória do JS, o que era o gargalo de "demora imensa".
 */
export const uploadCommunityVideo = async ({ asset, title, topic = null, area = null, section = 'updates' }) => {
  const user = await getCachedUser();
  if (!user) throw new Error('Usuário não autenticado');

  const mimeType = asset.mimeType || 'video/mp4';
  const ext = MIME_TO_EXT[mimeType] || 'mp4';
  const base = `${user.id}/${Date.now()}`; // 1ª pasta = userId (exigido pela policy de Storage)
  const storagePath = `${base}.${ext}`;
  const thumbPath = `${base}_thumb.jpg`;
  const durationSeconds = asset.duration ? Math.round(asset.duration / 1000) : null;

  // 1) Capa (best-effort)
  const thumbnailUrl = await generateAndUploadThumbnail(asset.uri, thumbPath);

  // 2) Vídeo em streaming direto para o endpoint REST do Storage (escrita exige auth).
  const session = (await supabase.auth.getSession()).data.session;
  if (!session?.access_token) throw new Error('Sessão ausente');
  const endpoint = `${process.env.EXPO_PUBLIC_SUPABASE_URL}/storage/v1/object/${UPLOAD_BUCKET}/${storagePath}`;
  let res;
  try {
    // Timeout generoso (2 min): vídeo de galeria pode ser grande/uplink lento,
    // mas sem teto a Promise pendura e o botão fica em "Publicando..." p/ sempre.
    res = await withTimeout(
      FileSystem.uploadAsync(endpoint, asset.uri, {
        httpMethod: 'POST',
        uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
        headers: {
          apikey: process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': mimeType,
          'x-upsert': 'false',
        },
      }),
      120000,
      'O envio do vídeo demorou demais. Verifique sua conexão e tente de novo.',
    );
  } catch (e) {
    await supabase.storage.from(UPLOAD_BUCKET).remove([storagePath, thumbPath]).catch(() => {});
    throw e;
  }
  if (res.status !== 200) {
    await supabase.storage.from(UPLOAD_BUCKET).remove([thumbPath]).catch(() => {});
    throw new Error(`Falha no upload do vídeo (HTTP ${res.status}).`);
  }

  // 3) Linha do feed.
  const { data, error } = await withTimeout(
    supabase
      .from('community_videos')
      .insert({
        user_id: user.id,
        source: VIDEO_SOURCE.UPLOAD,
        storage_path: storagePath,
        thumbnail_url: thumbnailUrl,
        title,
        topic,
        area,
        duration_seconds: durationSeconds,
        section,
      })
      .select('id, created_at')
      .single(),
    20000,
    'A publicação demorou demais. Verifique no feed se o vídeo já apareceu antes de tentar de novo.',
  ).catch((e) => {
    // No timeout o insert PODE ter persistido sem resposta — NÃO apagar o vídeo
    // do Storage (a linha pode estar referenciando). O onSettled reconcilia o feed.
    if (e?.name === 'TimeoutError') throw e;
    return { data: null, error: e };
  });

  if (error) {
    // Rollback: remove vídeo + capa se a linha falhar (não deixa órfão no Storage).
    await supabase.storage.from(UPLOAD_BUCKET).remove([storagePath, thumbPath]).catch(() => {});
    throw error;
  }
  return data;
};

// ── Conteúdo por autor (aba "Posts" do perfil) ──
const USER_VIDEOS_PAGE_SIZE = 20;

/**
 * Read-model enxuto p/ a lista de perfil: só o necessário p/ o card e o player.
 * NÃO carrega autor (o dono do perfil já é conhecido na tela) → sem JOIN, mais leve.
 * @returns {import('../types').ProfileVideoItem}
 */
const mapUserVideoRow = (r) => ({
  id: r.id,
  source: r.source,
  youtubeId: r.youtube_id,
  storagePath: r.storage_path,
  thumbnailUrl: r.thumbnail_url,
  title: r.title,
  durationSeconds: r.duration_seconds,
  section: r.section,
  status: r.status,
  viewsCount: r.views_count ?? 0,
  votesCount: r.votes_count ?? 0,
  createdAt: r.created_at,
});

/**
 * Vídeos de um autor (as duas seções), paginado por offset.
 * A RLS de SELECT já restringe a visibilidade (ativos p/ terceiros, todos p/ o dono).
 */
export const fetchUserVideos = async ({ userId, limit = USER_VIDEOS_PAGE_SIZE, offset = 0 }) => {
  if (!userId) return { items: [], hasMore: false };

  const { data, error, count } = await supabase
    .from('community_videos')
    .select(
      'id, source, youtube_id, storage_path, thumbnail_url, title, duration_seconds, section, status, views_count, votes_count, created_at',
      { count: 'exact' }
    )
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;

  const items = (data || []).map(mapUserVideoRow);
  return { items, hasMore: offset + limit < (count ?? items.length) };
};

/** Deriva o caminho da capa a partir do caminho do vídeo (`…/{ts}.ext` → `…/{ts}_thumb.jpg`). */
const deriveThumbPath = (storagePath) => {
  if (!storagePath) return null;
  const dot = storagePath.lastIndexOf('.');
  const base = dot > 0 ? storagePath.slice(0, dot) : storagePath;
  return `${base}_thumb.jpg`;
};

/**
 * Apaga um vídeo do usuário. Autorização real é a RLS (`delete-own`).
 * Ordem proposital: 1) linha (fonte de verdade do feed); 2) Storage best-effort.
 * Assim uma falha no Storage deixa, no pior caso, um arquivo órfão (GC barato) —
 * nunca um card fantasma apontando p/ arquivo já removido.
 */
export const deleteCommunityVideo = async (video) => {
  if (!video?.id) throw new Error('Vídeo inválido para remoção.');

  const { error } = await supabase.from('community_videos').delete().eq('id', video.id);
  if (error) throw error;

  if (video.source === VIDEO_SOURCE.UPLOAD && video.storagePath) {
    const paths = [video.storagePath, deriveThumbPath(video.storagePath)].filter(Boolean);
    await supabase.storage.from(UPLOAD_BUCKET).remove(paths).catch(() => {});
  }

  return { success: true };
};

/** URL pública para reproduzir um vídeo de upload (bucket público). */
export const getVideoPublicUrl = (storagePath) => publicUrl(storagePath);

/**
 * Adapta um `CommunityVideo` para o formato que o `FeedVideoOverlay` consome
 * (`post.videoUrl` / `post.imageUrl`).
 *
 * POR QUÊ: "Mais relevantes" reusa o player ÚNICO do feed em vez de abrir o
 * modal reel. O overlay é do domínio de POSTS e não conhece este módulo — em vez
 * de alargar o contrato dele (e com isso mexer no feed, que está estável), a
 * conversão acontece aqui. A URL de watch é montada de propósito no formato que
 * `utils/community/video` reconhece (`isYouTubeUrl` / `extractYoutubeId`).
 *
 * @returns {{id:string, videoUrl:string, imageUrl:string|null}|null} null quando
 *   não há mídia reproduzível — o overlay já trata `!videoUrl` retornando null.
 */
export const videoToFeedPost = (video) => {
  if (!video?.id) return null;

  const videoUrl =
    video.source === VIDEO_SOURCE.YOUTUBE
      ? (video.youtubeId ? `https://www.youtube.com/watch?v=${video.youtubeId}` : null)
      : getVideoPublicUrl(video.storagePath);

  if (!videoUrl) return null;

  return { id: video.id, videoUrl, imageUrl: video.thumbnailUrl || null };
};
